"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeOneTimeFee = computeOneTimeFee;
exports.computeBalanceTransferFee = computeBalanceTransferFee;
exports.computeRefiFee = computeRefiFee;
exports.computeRecordingFee = computeRecordingFee;
exports.getOfferRefiPayoffCents = getOfferRefiPayoffCents;
exports.getOfferRefiPositions = getOfferRefiPositions;
exports.getOfferRefiLenderName = getOfferRefiLenderName;
exports.getLineSizeRequiredToFundCents = getLineSizeRequiredToFundCents;
exports.getCommittedPayoffFeasibility = getCommittedPayoffFeasibility;
exports.getRefiAwareMinLineSizeCents = getRefiAwareMinLineSizeCents;
exports.getSliderOfferPayoffFeasibility = getSliderOfferPayoffFeasibility;
exports.getRefiAwareMinLineSizeCentsRounded = getRefiAwareMinLineSizeCentsRounded;
exports.roundCashAdditionalUpToNearestThousand = roundCashAdditionalUpToNearestThousand;
exports.getSliderOfferDetails = getSliderOfferDetails;
const math_1 = require("./math");
const HELOC_REFI_LIEN_POSITION = 2;
const ONE_CENT = 1;
// ---------------------------------------------------------------------------
// Compute functions (one per fee type)
// ---------------------------------------------------------------------------
/**
 * The one-time fee shown to the user = resolved upfront fee (origination-or-first-draw) applied to the line.
 * Pass the resolved oneTimeFeeDecimal.
 *
 * Formula: amountCents = Math.round(desiredLineSizeCents * feeDecimal)
 * Source:  extractOffers.py — origination charged on lineSize; first-draw on the upfront draw (= line at 100% draw).
 */
function computeOneTimeFee(desiredLineSizeCents, feeDecimal) {
    if (feeDecimal >= 1) {
        throw new Error(`feeDecimal must be a decimal (e.g. 0.049), got ${feeDecimal}`);
    }
    return {
        feeDecimal,
        amountCents: (0, math_1.roundDecimalProduct)(desiredLineSizeCents, feeDecimal),
    };
}
/**
 * Balance transfer fee = rate applied to the BT payoff amount. Additive.
 *
 * Formula: amountCents = Math.round(btPayoffCents * balanceTransferFeeDecimal)
 * Source:  avenBalanceTransferManager.ts
 *            amountWithFees = round(amountRequested * (btFeeRatio + 1))
 *            avenTxnFeeAmount = amountWithFees - amountRequested  (= amountRequested * btFeeRatio)
 * BT fee is charged at transaction time, not factored into line size derivation.
 */
function computeBalanceTransferFee(btPayoffCents, balanceTransferFeeDecimal) {
    if (balanceTransferFeeDecimal >= 1) {
        throw new Error(`balanceTransferFeeDecimal must be a decimal (e.g. 0.025), got ${balanceTransferFeeDecimal}`);
    }
    return {
        balanceTransferFeeDecimal,
        amountCents: (0, math_1.roundDecimalProduct)(btPayoffCents, balanceTransferFeeDecimal),
    };
}
/**
 * Refi (prior lien payoff) fee = rate applied to the refi payoff amount. Additive.
 *
 * Formula: amountCents = Math.round(refiPayoffCents * priorLienPayoffFeeDecimal)
 * Source:  helocRefiManager.ts
 *            return round(priorLienPayoffFee * refiAmountInDollars, 2)
 * Refi fee is charged at transaction time, not factored into line size derivation.
 */
function computeRefiFee(refiPayoffCents, priorLienPayoffFeeDecimal) {
    if (priorLienPayoffFeeDecimal >= 1) {
        throw new Error(`priorLienPayoffFeeDecimal must be a decimal (e.g. 0.03), got ${priorLienPayoffFeeDecimal}`);
    }
    return {
        priorLienPayoffFeeDecimal,
        amountCents: (0, math_1.roundDecimalProduct)(refiPayoffCents, priorLienPayoffFeeDecimal),
    };
}
/**
 * Recording fee at a given line size, derived from the tax components.
 *
 * Formula: amountCents = Math.round(recordingTaxVariableDecimal * desiredLineSizeCents + recordingTaxFixedCents)
 * Source:  closingChargesComputation.getRecordingChargesComponentsForLineSize
 */
function computeRecordingFee(desiredLineSizeCents, recordingTaxFixedCents, recordingTaxVariableDecimal) {
    return {
        amountCents: (0, math_1.roundDecimalProduct)(desiredLineSizeCents, recordingTaxVariableDecimal) + recordingTaxFixedCents,
    };
}
function getHelocRefiLienInfo(details) {
    const helocRefiLienInfo = details?.lienRefiDetails?.find((lienRefiDetail) => lienRefiDetail.lienPosition === HELOC_REFI_LIEN_POSITION)?.lienInfo;
    if (helocRefiLienInfo) {
        return helocRefiLienInfo;
    }
    // TODO: Remove this fallback after the backend populates lienRefiDetails for second-lien refi details.
    return details?.secondLienInfo ?? null;
}
function getHelocRefiPayoffCents(details) {
    const helocRefiLienInfo = getHelocRefiLienInfo(details);
    const payoffDollars = helocRefiLienInfo?.lienBalanceAmount ?? helocRefiLienInfo?.lienAttestedBalanceAmount ?? 0;
    return (0, math_1.roundDecimalProduct)(payoffDollars, 100);
}
function getHelocRefiLenderName(details) {
    const helocRefiLienDetails = details?.lienRefiDetails?.find((lienRefiDetail) => lienRefiDetail.lienPosition === HELOC_REFI_LIEN_POSITION);
    if (helocRefiLienDetails) {
        return helocRefiLienDetails.lenderName || helocRefiLienDetails.lienInfo.lienLenderName || null;
    }
    // TODO: Remove this fallback after the backend populates lienRefiDetails for second-lien refi details.
    return details?.secondLienInfo?.lienLenderName ?? null;
}
function getOfferRefiPayoffCents(offer) {
    // Canonical source is `lienRefiOfferTerms.totalBalance` — the sum of every refinanced lien
    // balance (dollars). Fall back to the legacy second-lien-only fields for offers that predate it.
    if (offer?.lienRefiOfferTerms) {
        return (0, math_1.roundDecimalProduct)(offer.lienRefiOfferTerms.totalBalance, 100);
    }
    const payoffFromHelocRefiDetails = getHelocRefiPayoffCents(offer?.helocRefiDetails);
    if (payoffFromHelocRefiDetails > 0) {
        return payoffFromHelocRefiDetails;
    }
    return (0, math_1.roundDecimalProduct)(offer?.secondLienAttestedPayoffDollars ?? 0, 100);
}
/**
 * The ordered lien positions an offer refinances (empty = non-refi). Drives the slider's
 * group key so a base offer and each refi variant at the same term + fee form distinct tracks.
 */
function getOfferRefiPositions(offer) {
    const positions = offer?.lienRefiOfferTerms?.refiLiensDetails?.map((detail) => detail.lienPosition) ?? [];
    if (positions.length > 0) {
        return [...positions].sort((a, b) => a - b);
    }
    // Legacy refi offers predate lienRefiOfferTerms: any positive payoff implies the position-2 lien.
    return getOfferRefiPayoffCents(offer) > 0 ? [2] : [];
}
function getOfferRefiLenderName(offer) {
    // Canonical source: the lender names across every refinanced lien, joined for display.
    const refiLiensDetails = offer?.lienRefiOfferTerms?.refiLiensDetails;
    if (refiLiensDetails && refiLiensDetails.length > 0) {
        const lenderNames = refiLiensDetails.map((detail) => detail.lenderName).filter((name) => name !== null);
        return lenderNames.length > 0 ? lenderNames.join(' + ') : null;
    }
    const lenderNameFromHelocRefiDetails = getHelocRefiLenderName(offer?.helocRefiDetails);
    if (lenderNameFromHelocRefiDetails) {
        return lenderNameFromHelocRefiDetails;
    }
    return offer?.secondLienLenderName ?? null;
}
/**
 * Represents `policy.STATE_TAX_LINE_SIZE_ZERO_TAX_CUTOFF`, which is the line size cutoff under which Aven
 * absorbs recording tax, so the tax is not part of the line-size requirement. As of 2026-08-18, in every
 * slider-compatible policy (policy.COMPATIBLE_WITH_PREQUAL_SLIDER = true), every state that charges recording
 * tax sets $0.
 *
 * TODO: It's bad to just set a constant here because this represents a policy param; we should send the actual
 *  policy value to the frontend to use when determining which slider values require a recording tax and which
 *  don't. The problem is we'd need to include the state in the calculation as well and it's not easily in scope
 *  when we need it. At least using $0 here would hide offers that would be approved, rather than show offers
 *  that wouldn't be approved, if those policies increase STATE_TAX_LINE_SIZE_ZERO_TAX_CUTOFF to > $0.
 */
const RECORDING_TAX_FREE_LINE_SIZE_CUTOFF_CENTS = 0;
/**
 * Smallest gross line size that still nets `amountToFundCents` after the upfront fee and recording tax.
 * Returns math.inf when the fee and tax together consume the whole line.
 */
function getLineSizeRequiredToFundCents(params) {
    const { amountToFundCents, upfrontDrawFeeDecimal, minDrawDownDecimal, recordingTaxFixedCents, recordingTaxVariableDecimal } = params;
    // A NaN amount would otherwise poison the requirement into NaN, which compares false against the
    // line size and would silently pass everything; treat it as "nothing committed" instead.
    if (Number.isNaN(amountToFundCents)) {
        return 0;
    }
    if (amountToFundCents <= 0) {
        return 0;
    }
    // The upfront fee is only charged on the drawn portion of the line, so scale it by the minimum
    // draw-down before netting it out of the line — same as `minDrawDown * upfrontFee.percent` in Python.
    const effectiveUpfrontDrawFeeDecimal = minDrawDownDecimal * upfrontDrawFeeDecimal;
    const minLineSizeCents = amountToFundCents / (1 - effectiveUpfrontDrawFeeDecimal);
    if (minLineSizeCents <= RECORDING_TAX_FREE_LINE_SIZE_CUTOFF_CENTS) {
        return Math.ceil(minLineSizeCents);
    }
    const netOfFeeAndTax = 1 - effectiveUpfrontDrawFeeDecimal - recordingTaxVariableDecimal;
    if (netOfFeeAndTax <= 0) {
        return Infinity;
    }
    return Math.ceil((amountToFundCents + recordingTaxFixedCents) / netOfFeeAndTax);
}
/**
 * Compute the stricter gross-line requirement from the refinance-only floor and the
 * fee- and tax-adjusted refinance plus selected debt-swap payoffs. Comparing this
 * gross requirement to a net cash-axis value would charge the upfront fee twice.
 * `refiRequiredGrossLineSizeCents` is the preliminary refinance floor.
 */
function getCommittedPayoffFeasibility(params) {
    const { refiPayoffCents, refiRequiredGrossLineSizeCents, debtSwapPayoffCents, upfrontDrawFeeDecimal, refiBufferDecimal, debtSwapBufferDecimal, recordingTaxFixedCents, recordingTaxVariableDecimal, minDrawDownDecimal, maxGrossLineSizeCents, } = params;
    const totalCommittedPayoffCents = refiPayoffCents * (1 + refiBufferDecimal) + debtSwapPayoffCents * (1 + debtSwapBufferDecimal);
    const committedPayoffRequirementCents = getLineSizeRequiredToFundCents({
        amountToFundCents: totalCommittedPayoffCents,
        upfrontDrawFeeDecimal,
        minDrawDownDecimal,
        recordingTaxFixedCents,
        recordingTaxVariableDecimal,
    });
    const requiredGrossLineSizeCents = Math.max(refiRequiredGrossLineSizeCents, committedPayoffRequirementCents);
    return {
        requiredGrossLineSizeCents,
        maxGrossLineSizeCents,
        isFeasible: requiredGrossLineSizeCents <= maxGrossLineSizeCents,
    };
}
/** Preliminary gross refinance floor; the committed-payoff requirement also nets fees and tax. */
function getRefiAwareMinLineSizeCents(refiPayoffCents, priorLienPayoffFee, secondLienBalanceBufferPercent) {
    if (refiPayoffCents <= 0) {
        return 0;
    }
    // Backend hardcut uses strict inequality: line size must be greater than this threshold.
    return Math.floor(refiPayoffCents * (1 + secondLienBalanceBufferPercent) + refiPayoffCents * priorLienPayoffFee + ONE_CENT);
}
function getSliderOfferPayoffFeasibility(offer, debtSwapPayoffCents, debtSwapBufferDecimal, roundRefiAdditionalFloor = false) {
    const refiRequiredGrossLineSizeCents = roundRefiAdditionalFloor
        ? getRefiAwareMinLineSizeCentsRounded(offer.refiPayoffCents, offer.priorLienPayoffFee, offer.secondLienBalanceBufferPercent)
        : getRefiAwareMinLineSizeCents(offer.refiPayoffCents, offer.priorLienPayoffFee, offer.secondLienBalanceBufferPercent);
    return getCommittedPayoffFeasibility({
        refiPayoffCents: offer.refiPayoffCents,
        refiRequiredGrossLineSizeCents,
        debtSwapPayoffCents,
        upfrontDrawFeeDecimal: offer.oneTimeFeeDecimal,
        minDrawDownDecimal: offer.minDrawDownDecimal,
        refiBufferDecimal: offer.secondLienBalanceBufferPercent,
        debtSwapBufferDecimal,
        recordingTaxFixedCents: offer.recordingTaxFixedCents,
        recordingTaxVariableDecimal: offer.recordingTaxVariableDecimal,
        maxGrossLineSizeCents: offer.maxLineSizeCents,
    });
}
/**
 * Slider-floor variant of {@link getRefiAwareMinLineSizeCents}: rounds the refi additional-limit
 * floor (balance buffer + recording) up to the nearest $1,000 so the slider never exposes a line
 * that's a hair above the payoff-with-fee. The hardcut mirror above stays exact — this rounding
 * belongs to the slider floor, not the hardcut.
 */
function getRefiAwareMinLineSizeCentsRounded(refiPayoffCents, priorLienPayoffFee, secondLienBalanceBufferPercent) {
    const refiMinLineSizeCents = getRefiAwareMinLineSizeCents(refiPayoffCents, priorLienPayoffFee, secondLienBalanceBufferPercent);
    if (refiPayoffCents <= 0) {
        return refiMinLineSizeCents;
    }
    const payoffWithFeeCents = (0, math_1.roundDecimalProduct)(refiPayoffCents, 1 + priorLienPayoffFee);
    return payoffWithFeeCents + Math.ceil((refiMinLineSizeCents - payoffWithFeeCents) / 100000) * 100000;
}
/**
 * Round the net cash floor (the additional amount above the committed payoff) up to the nearest
 * $1,000. The gross line-size floor is rounded in {@link getRefiAwareMinLineSizeCentsRounded}, but
 * the Aven-Cash slider exposes the net cash — where the upfront-fee deduction leaves a non-round
 * amount (e.g. $1,030.74 instead of $1,000). This normalizes the displayed "Cash" back to a clean
 * $1,000 multiple without disturbing the no-refi or payoff-exceeds-floor cases.
 */
function roundCashAdditionalUpToNearestThousand(minCashAndPayoffCents, payoffCents) {
    if (payoffCents <= 0 || minCashAndPayoffCents <= payoffCents) {
        return minCashAndPayoffCents;
    }
    return payoffCents + Math.ceil((minCashAndPayoffCents - payoffCents) / 100000) * 100000;
}
/**
 * Compute per-offer details for the prequal slider, including fee breakdowns
 * and eligibility. Pure function — no DB calls, no async.
 *
 * Takes SliderOfferConstants[] (from extractSliderOfferConstants) so
 * validation and term-min computation live in one place.
 *
 * The slider axis is net cash, so desiredLineSizeCents is derived from net-cash-and-payoff via the
 * resolved oneTimeFeeDecimal. That line is what the slider submits.
 */
function getSliderOfferDetails(userInputs, uwResult, debtSwapTradelines, sliderOfferConstants) {
    if (sliderOfferConstants.length === 0) {
        return [];
    }
    const btPayoffCents = debtSwapTradelines.reduce((sum, t) => sum + t.balanceCents, 0);
    return sliderOfferConstants.map((offer) => {
        // The slider axis is the net cash the applicant receives, so it deducts the whole upfront fee:
        // convert net-cash-and-payoff → line via oneTimeFeeDecimal.
        const cashAndPayoffCents = userInputs.cashAmountCents + btPayoffCents + offer.refiPayoffCents;
        const desiredLineSizeCents = (0, math_1.cashAndPayoffToLineSizeCents)(cashAndPayoffCents, offer.oneTimeFeeDecimal, offer.recordingTaxFixedCents, offer.recordingTaxVariableDecimal);
        const refiMinLineSizeCents = getRefiAwareMinLineSizeCents(offer.refiPayoffCents, offer.priorLienPayoffFee, offer.secondLienBalanceBufferPercent);
        const effectiveMinLineSizeCents = Math.max(offer.minLineSizeCents, refiMinLineSizeCents);
        return {
            constants: {
                offerIdentifier: offer.identifier,
                minLineSizeCents: effectiveMinLineSizeCents,
                maxLineSizeCents: offer.maxLineSizeCents,
                refiPayoffCents: offer.refiPayoffCents,
                refiLenderName: offer.refiLenderName,
            },
            computed: {
                isEligible: desiredLineSizeCents >= effectiveMinLineSizeCents && desiredLineSizeCents <= offer.maxLineSizeCents,
                desiredLineSizeCents,
                oneTimeFee: computeOneTimeFee(desiredLineSizeCents, offer.oneTimeFeeDecimal),
                balanceTransferFee: computeBalanceTransferFee(btPayoffCents, offer.balanceTransferFeePercent),
                refiFee: computeRefiFee(offer.refiPayoffCents, offer.priorLienPayoffFee),
                recordingFee: computeRecordingFee(desiredLineSizeCents, offer.recordingTaxFixedCents, offer.recordingTaxVariableDecimal),
            },
        };
    });
}
